import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./Documents.css"
import { AddButton, AddButton2, ClosePopup, EditBtn, ErrorComp, ForMnSteps, HelpBtn, LinkSmart, Loading, Popup, RadioCheckButton, SafeEditBtn, SaveButton, SaveCancelBox, SquareButton, TabSwitch, UploadInput } from "./Common";
import { LocaleContext, LoginContext, NavigateLockContext, OverlayContext, ScrollContext, StateContext } from "./App";
import { Link, resolvePath, useLocation } from "react-router-dom";
import { LoadState, UploadInputState } from "./enums.mjs";
import { genId, isTabActive, uploadFiles } from "./util";
import { useAutoLoad, useSafeEdit } from "./hooks";
import { maxNoOfDocumentsInOneUpload } from "./globals.mjs";


export class Document {
    constructor(name) {
        this.name = name;
        this.file = null;
        this.filename = "";
        this.onServer = false;
        this.url = "";
        this.uploadInputState = UploadInputState.SELECT;
        this.errorMsg = "";
        this.done = false;
        this.error = false;
        this.share = false;
        this.oldShare = false;
        this.shareHelpDisplayed = false;
        this.shareManagement = false;
        this.fromCustomer = true;

        this.revisedShown = false;
    }
}

export function Documents(props) {
    // VARS:

    const [id, setId] = useState(genId());
    
    const locale = useContext(LocaleContext);
    const state = useContext(StateContext);

    const location = useLocation();

    const [docsSaving, setDocsSaving] = useState(false);
    const [docsSaved, setDocsSaved] = useState(false);
    const [docsNotSaved, setDocsNotSaved] = useState(false);
    
    const [docsUploadProgress, setDocsUploadProgress] = useState(0);

    const [docObj, setDocObj] = useState(getPlainDocObj());
    const filesToDelete = useRef([]);

    const [editable, setEditable] = useState(false);
    const editableRef = useRef(editable);

    const [oldDocObj, setOldDocObj] = useState(null);

    const [goodConductCertificateUrl, setGoodConductCertificateUrl] = useState(null);
    const [goodConductCertificateName, setGoodConductCertificateName] = useState(null);
    const [goodConductHelpDisplayed, setGoodConductHelpDisplayed] = useState(null);


    // FUNCTIONS:

    function getPlainDocObj() {
        return {
            resume: new Document(locale.msgs.RESUME),
            goodConduct: new Document(locale.msgs.GOOD_CONDUCT),
            photo: new Document(locale.msgs.PHOTO),
            references: new Document(locale.msgs.REFERENCES),
            firstAidCourse: new Document(locale.msgs.FIRST_AID_COURSE)
        };
    }

    function setEditableFunc(e) {
        setEditable(e);
        editableRef.current = e;

        if(props.management) {
            props.onEditChange(e);
        }

        safeEdit.setDisplayEditTooltip(false);
    }

    function handleDocsSaveClick() {
        if(docsSaving)
            return;

        state.fetching.current = true;
        setDocsSaved(false);
        setDocsNotSaved(false);
        setDocsSaving(true);
        setDocsUploadProgress(0);

        let filesToUpload = [];
        let shareInfo = [];

        for(let [k, v] of Object.entries(docObj)) {
            addFileToUploadObjects(k, v, filesToUpload, shareInfo);
        }

        let url = "/user/upload_documents";
        let addInfo = null;

        if(props.management) {
            url = "/user/management/upload_documents";
            addInfo = [["user_id", props.userId]];
        }

        uploadFiles(filesToUpload, filesToDelete.current, shareInfo, url, (progress) => {
            if(progress == 100) {
                progress = 99;
            }

            setDocsUploadProgress(progress);
        }, (response) => {
            state.fetching.current = false;
            setDocsSaving(false);
            setDocsSaved(true);
            setEditableFunc(false);

            handleDocsFetched(response.docs);

            if(props.management) {
                props.setUser({...props.user, documents_checked: response.documents_checked});
            }

            handleDocUploadFinished(true);
        }, () => {
            state.fetching.current = false;
            setDocsSaving(false);
            setDocsNotSaved(true);
            handleDocUploadFinished(false);
        },
        0,
        addInfo);
    }

    function addFileToUploadObjects(k, v, filesToUpload, shareInfo) {
        if(v.uploadInputState == UploadInputState.SELECTED)
        {
            filesToUpload.push({file: v.file, category: k});

            setDocObj(curDocObj => (
                {...curDocObj, [k]: {...curDocObj[k], uploadInputState: UploadInputState.UPLOADING, done: false, error: false, errorMsg: ""}}
            ));

            if(props.management) {
                shareInfo.push({ category: k, shareManagement: v.shareManagement});
            }
            else {
                shareInfo.push({ category: k, share: v.share});
            }
        }
        else if(v.onServer){
            if(props.management) {
                shareInfo.push({ category: k, shareManagement: v.shareManagement});
            }
            else {
                shareInfo.push({ category: k, share: v.share});
            }
        }
    }

    function handleEdit() {
        if(props.readOnly) {
            return;
        }

        if(!editable) {
            setOldDocObj(docObj);
            filesToDelete.current = [];
            setEditableFunc(true);
            setDocsSaved(false);
            setDocsNotSaved(false);
        }
    }

    function handleCancel() {
        if(docsSaving)
            return;

        setDocObj(oldDocObj);
        setDocsSaved(false);
        setDocsNotSaved(false);

        setDocObj(curDocObj =>  {
            const newDocObj = {...curDocObj};

            for(let [k, v] of Object.entries(newDocObj)) {
                newDocObj[k] = {...newDocObj[k], errorMsg: ""};
            }
            
            return newDocObj;
        });
        
        setEditableFunc(false);
    }

    function isSavable() {
        if(!editable || oldDocObj === null)
            return [false, ""];

        // Check the maximum number of documents
        let noOfDocsToBeUploaded = 0;
        for(let [k, v] of Object.entries(docObj)) {
            if(v.uploadInputState == UploadInputState.SELECTED)
            {
                noOfDocsToBeUploaded++;
            }
        }
        if(noOfDocsToBeUploaded > maxNoOfDocumentsInOneUpload) {
            return [false, locale.msgs.MAX_NO_OF_DOCUMENTS.replace("${{documents}}", maxNoOfDocumentsInOneUpload)];
        }

        // Never allow to save if there is a custom / revised file input without a file that is visible
        // for(let [k, v] of Object.entries(docObj)) {
        //     if(k.startsWith("custom_") || k.endsWith("Revised")) {
        //         if(v.uploadInputState !== UploadInputState.SELECTED && v.uploadInputState !== UploadInputState.UPLOADED) {
        //             return [false, ""];
        //         }
        //     }
        // }

        // Always allow to save if there is at least one file input to remove
        if(filesToDelete.current.length !== 0) {
            return [true, ""];
        }

        const oldEntries = Object.entries(getDocObjWithoutEmptyDocs(oldDocObj));
        const newEntries = Object.entries(getDocObjWithoutEmptyDocs(docObj));

        if (newEntries.length != oldEntries.length)
        {
            /*
            for(let [k, v] of Object.entries(docObj)) {
                if(k.startsWith("custom_")) {
                    if(v.uploadInputState !== UploadInputState.SELECTED)
                        return [true, ""];
                }
            }
*/

            return [true, ""];
        }

        for(let i = 0; i < oldEntries.length; i++) {
            const oldEntry = oldEntries[i];
            const newEntry = newEntries[i];

            // Compare the property names
            if(oldEntry[0] != newEntry[0])
            {
                return [true, ""];
            }

            // console.log("Name: " + oldEntry[0]);

            // Get the entries of the document
            const oldDocumentEntries = Object.entries(oldEntry[1]);
            const newDocumentEntries = Object.entries(newEntry[1]);

            for(let j = 0; j < newDocumentEntries.length; j++) {
                const oldDocumentEntry = oldDocumentEntries[j];
                const newDocumentEntry = newDocumentEntries[j];

                // Ignore unrelevant properties
                if(oldDocumentEntry[0] === "revisedShown") {
                    continue;
                }

                if(oldDocumentEntry[1] != newDocumentEntry[1])
                {
                    return [true, ""];
                }
            }

            // console.log(oldKey);
/*
            // Check if any document is selected for upload
            if(v.uploadInputState === UploadInputState.SELECTED) {
                return [true, ""];
            }

            if(v.onServer) {
                // Check if the share state has changed
                if(v.share != v.oldShare) {
                    return [true, ""];
                }

                if(k.startsWith("custom_")) {
                    if(v.uploadInputState !== UploadInputState.SELECTED)
                        return [true, ""];
                }
                else {
                    // Check if a document should be deleted
                    if(v.uploadInputState === UploadInputState.SELECT) {
                        // Document should be deleted
                        return [true, ""];
                    }
                }
            }
            */
        }

        return [false, ""];
    }

    function getDocObjWithoutEmptyDocs(docObj) {
        let result = {};

        for(let [k, v] of Object.entries(docObj)) {
            if(v.uploadInputState !== UploadInputState.SELECT) {
                result[k] = v;
            }
        }

        return result;
    }

    function areCustomDocumentsSelected() {
        for(let [k, v] of Object.entries(docObj)) {
            
        }

        return true;
    }

    function getDocNameTranslation(name) {
        if(name === "resume") {
            return locale.msgs.RESUME;
        }
        else if(name === "goodConduct") {
            return locale.msgs.GOOD_CONDUCT;
        }
        else if(name === "photo") {
            return locale.msgs.PHOTO;
        }
        else if(name === "references") {
            return locale.msgs.REFERENCES;
        }
        else if(name === "firstAidCourse") {
            return locale.msgs.FIRST_AID_COURSE;
        }
        else if(name === "resumeRevised") {
            return locale.msgs.RESUME + " (" + locale.msgs.REVISED + ")";
        }
        else if(name === "goodConductRevised") {
            return locale.msgs.GOOD_CONDUCT + " (" + locale.msgs.REVISED + ")";
        }
        else if(name === "photoRevised") {
            return locale.msgs.PHOTO + " (" + locale.msgs.REVISED + ")";
        }
        else if(name === "referencesRevised") {
            return locale.msgs.REFERENCES + " (" + locale.msgs.REVISED + ")";
        }
        else if(name === "firstAidCourseRevised") {
            return locale.msgs.FIRST_AID_COURSE + " (" + locale.msgs.REVISED + ")";
        }
        else if(name.endsWith("Revised")){
            return locale.msgs.ADDITIONAL_DOCUMENT + " (" + locale.msgs.REVISED + ")";
        }
        else {
            return locale.msgs.ADDITIONAL_DOCUMENT;
        }
    }

    function handleDocsFetched(json) {
        const newDocObj = getPlainDocObj();

        for(let [k, v] of Object.entries(json)) {
            newDocObj[k] = new Document(getDocNameTranslation(k));

            newDocObj[k].onServer = true;
            newDocObj[k].filename = v.name;
            newDocObj[k].url = v.url;
            newDocObj[k].uploadInputState = UploadInputState.UPLOADED;
            newDocObj[k].share = v.share;
            newDocObj[k].oldShare = v.share;
            newDocObj[k].shareManagement = v.share_management;
            newDocObj[k].fromCustomer = v.from_customer;

            if(!k.endsWith("Revised")) {
                const kRevised = k + "Revised";

                for(let [k2, v2] of Object.entries(json)) {
                    if(k2 === kRevised) {
                        newDocObj[k].revisedShown = true;
                    }
                }
            }
        }

        const iterDocObj = {...newDocObj};

        if(props.management && props.onDocumentsBackendChange) {
            props.onDocumentsBackendChange(newDocObj);
        }

        if(!editableRef.current) {
            setDocObj(newDocObj);
        }
        
        setOldDocObj(newDocObj);
    }

    function handleDocUploadFinished(saved) {
        setDocObj((curDocObj) => {
            const newDocObj = {...curDocObj};

            for(let [k, v] of Object.entries(curDocObj)) {
                if(v.uploadInputState == UploadInputState.UPLOADING) {
                    if(saved) {
                        // Uploading was successful
                        newDocObj[k] = {...curDocObj[k], uploadInputState: UploadInputState.UPLOADED, done: true, error: false, errorMsg: "", onServer: true};
                    } else {
                        newDocObj[k] = {...curDocObj[k], uploadInputState: UploadInputState.SELECTED, done: true, error: true, errorMsg: locale.msgs.UPLOAD_FAILED};
                    }
                } 
            }

            return newDocObj;
        });
    }

    function getCustomCount() {
        let result = 0;

        for(let k in docObj) {
            if(k.startsWith("custom_")) {
                let num = Number(k.split("custom_")[1]);

                if(num > result) {
                    result = num;
                }
            }
        }

        return result;
    }

    function handleAddClick() {
        if(!editable) {
            return;
        }

        let newCount = getCustomCount() + 1;

        if(newCount > 10) {
            console.log("Maximum number of custom documents reached");
            return;
        }

        let key = String("custom_" + newCount);

        const newDocObj = {...docObj, [key]: new Document(locale.msgs.ADDITIONAL_DOCUMENT)};
        setDocObj(newDocObj);
    }

    function addFileToDeleteList(category) {
        // Avoid duplicates
        for(const c of filesToDelete.current) {
            if(c === category) {
                return;
            }
        }

        filesToDelete.current.push(category);
    }

    function handleDeleteOrigAndRevised(k, v, kRevised, vRevised) {
        if(v.onServer) {
            addFileToDeleteList(k);
        }

        setDocObj((curDocObj) => {
            const newDocObj = {...curDocObj};

            if(k.startsWith("custom_")) {
                delete newDocObj[k];
            }
            else {
                newDocObj[k] = { ...curDocObj[k], revisedShown: false, onServer: false};
            }

            if(vRevised) {
                delete newDocObj[kRevised];

                if(vRevised.onServer) {
                    addFileToDeleteList(kRevised);
                }
            }

            return newDocObj;
        });
    }


    // EFFECTS:

    useEffect(() => {
        editableRef.current = editable;
    }, [editable]);

    const [loadState, setLoadState] = useAutoLoad((json, scheduled, init) => {
        // On load success
        handleDocsFetched(json.editable_documents);

        if (json.hasOwnProperty("good_conduct_certificate_url")) {
            setGoodConductCertificateUrl(json.good_conduct_certificate_url);
            setGoodConductCertificateName(json.good_conduct_certificate_name);
        }
        else {
            setGoodConductCertificateUrl(null);
            setGoodConductCertificateName(null);
        }

        let atLeastOneOnServer = false;
        for(let [k, v] of Object.entries(json)) {
            atLeastOneOnServer = true;
        }

        if(!atLeastOneOnServer && !props.noAutoEdit && init) {
            handleEdit();
        }
    }, () => {
        // On load error
    }, () => {
        // On before load

        let url = "/user/documents";
        
        if(props.management) {
            if(!isTabActive(props.tabBasepath, location)) {
                return null;
            }

            url = "/user/management/documents?user_id=" + props.userId;
        }
        
        return url;
    }, props.management ? [props.userId] : [], true, false, false, true);

    // Update the names if the language changes
    useLayoutEffect(() => {
        setDocObj((curDocObj) => {
            const newDocObj = {...curDocObj};

            newDocObj.resume.name = locale.msgs.RESUME;
            newDocObj.goodConduct.name = locale.msgs.GOOD_CONDUCT;
            newDocObj.photo.name = locale.msgs.PHOTO;
            newDocObj.references.name = locale.msgs.REFERENCES;
            newDocObj.firstAidCourse.name = locale.msgs.FIRST_AID_COURSE;
    
            return newDocObj;
        });
    }, [locale]);

    const safeEdit = useSafeEdit(editable, [docObj], [oldDocObj], props.lockNavigation);


    // RENDER:

    if(loadState === LoadState.LOAD_ERROR) {
        return (
            <div className="flex_col">
                <ErrorComp text={locale.msgs.GENERAL_ERROR_DESCR} />
            </div>
        );
    }
    else if(loadState === LoadState.LOADING) {
        return (
            props.smallLoading ?
                <div className="loader margin_top_low_low"/>
            :
                <div className="documents_loading flex_col side_padding">
                    <Loading management={props.management} />
                </div>
        );
    }

    let result = null;
    
    if(docObj != null) {
        const [isSavableVar, errText] = isSavable();

        let boxes = [];
        let count = 0;

        for(let [k, v] of Object.entries(docObj)) {
            if(k.endsWith("Revised") || v.toRemove || (props.hideEmpty === true && !v.onServer))
                continue;

            count++;

            const kRevised = k + "Revised";
            const vRevised = docObj[kRevised];

            const sharable = k !== "goodConduct";

            const releaseInfoVisible = !editable && sharable && v.filename != "" && (!props.management || v.share || v.fromCustomer === false);

            const showMnReleased = props.management && sharable && v.filename != "" && v.share && v.fromCustomer;
            const showMnNotReleased = props.management && sharable && v.filename != "" && !v.share && v.fromCustomer;

            boxes.push(
                <div className={"doc_upload_input_box" +
                    (count === 1 ?
                        editable || v.uploadInputState === UploadInputState.SELECT ? " doc_upload_input_box_first_high" : " doc_upload_input_box_first_low"
                    :
                        (!editable && v.uploadInputState !== UploadInputState.SELECT ?
                            " doc_upload_input_box_no_edit_file_selected"
                        :
                            " "))}
                >

                    <UploadInput name={v.name} filename={v.filename}
                            uploadProgress={docsUploadProgress} editable={editable} state={v.uploadInputState}
                            errorMsg={v.errorMsg}
                            url={v.url}
                            
                            onStateChange={(state) => {
                                setDocObj((curDocObj) => ({...curDocObj, [k]: { ...curDocObj[k], uploadInputState: state, errorMsg: "" }}));
                                setDocsNotSaved(false);
                                setDocsSaved(false);

                                if(state === UploadInputState.SELECT) {
                                    handleDeleteOrigAndRevised(k, v, kRevised, vRevised);
                                }
                            }}
                            onFileChange={(file) => setDocObj((curDocObj) => ({...curDocObj, [k]: { ...curDocObj[k], file: file, filename: (file === null ? "" : file.name),
                                                                                                    fromCustomer: !props.management }})) }
                            removeBtnPresent={k.startsWith("custom_") /* getCustomCount() !== 0 */ && editable}
                            removable={ k.startsWith("custom_") }
                            onRemoveBtnClick={ () => {
                                setDocsNotSaved(false);
                                setDocsSaved(false);
                                handleDeleteOrigAndRevised(k, v, kRevised, vRevised);
                            }}
                    />

                    { showMnReleased ?
                        <div className={"document_share_suc_box" + (editable && kRevised.startsWith("custom_") ? " document_share_suc_box_custom_edit" : "")}>
                            <img className="passing_released_img" src="/common/icon_done_green.svg"></img>
                            <div className="passing_released_img_text text_success_2">{locale.msgs.PASSING_RELEASED_MN}</div>
                        </div>

                    :
                        null
                    }

                    { showMnNotReleased ?
                        <div className={"document_share_suc_box" + (editable && kRevised.startsWith("custom_") ? " document_share_suc_box_custom_edit" : "")}>
                            <img className="passing_released_img_err" src="/common/icon_close_red.svg"></img>
                            <div className="passing_released_img_text passing_released_img_text_err text_error_2">
                                {locale.msgs.PASSING_NOT_RELEASED_MN}
                            </div>
                        </div>
                    :
                        null
                    }

                    { v.revisedShown ?
                        <div className={"doc_upload_input_revised" +
                                (kRevised.startsWith("custom_") && editable ? " doc_upload_input_revised_custom_edit" : "") +
                                (vRevised.uploadInputState !== UploadInputState.SELECT && editable ? " doc_upload_input_revised_custom_edit_not_select" : "") }>
                            <UploadInput name={vRevised.name} filename={vRevised.filename}
                                    uploadProgress={docsUploadProgress} editable={editable} state={vRevised.uploadInputState}
                                    errorMsg={vRevised.errorMsg}
                                    url={vRevised.url}
                                    
                                    onStateChange={(state) => {
                                        setDocObj((curDocObj) => ({...curDocObj, [kRevised]: { ...curDocObj[kRevised], uploadInputState: state, errorMsg: "" }}));
                                        setDocsNotSaved(false);
                                        setDocsSaved(false);

                                        if(state === UploadInputState.SELECT) {
                                            if(vRevised.onServer) {
                                                addFileToDeleteList(kRevised);
                                            }

                                            setDocObj((curDocObj) => {
                                                const newDocObj = {...curDocObj};
                                                // delete newDocObj[kRevised];
                                                newDocObj[kRevised] = {...newDocObj[kRevised], onServer: false};
                                                return newDocObj;
                                            });
                                        }
                                    }}
                                    onFileChange={(file) => setDocObj((curDocObj) => ({...curDocObj, [kRevised]: { ...curDocObj[kRevised], file: file, filename: (file === null ? "" : file.name)} })) }
                                    removeBtnPresent={false}
                                    removable={false}
                                    onRemoveBtnClick={ () => {
                                        setDocsNotSaved(false);
                                        setDocsSaved(false);

                                        if(vRevised.onServer) {
                                            addFileToDeleteList(kRevised);
                                        }
                                        
                                        const newDocObj = {...docObj};
                                        delete newDocObj[kRevised];
                                        setDocObj(newDocObj);
                                    }}
                            />
                        </div>
                    :
                        null
                    }

                    { sharable ?
                        <div className={"doc_upload_input_extra_box" + (v.revisedShown ? " doc_upload_input_extra_box_revised" : "") +
                                (!v.revisedShown && k.startsWith("custom_") && editable ? " doc_upload_input_extra_box_custom_edit" : "") +
                                (v.revisedShown && kRevised.startsWith("custom_") && editable ? " doc_upload_input_extra_box_revised_custom_edit" : "")}>
                            <div className={"doc_upload_input_extra" + (editable ? "" : " doc_upload_input_extra_abs")}>
                                {
                                    editable && v.filename != "" && (!props.management || (props.management && (v.fromCustomer && v.share) || (!v.fromCustomer))) ? 
                                
                                    <div className={"share_box" + (props.management ? " share_box_management" : "")}>
                                        <div className="share_radio_box">
                                            <RadioCheckButton tiny={true} smallFont={true} type="radio" text={locale.msgs.NOT_SHARE}
                                                    id={k + "_free_doc_1_" + id} name={k + "_free_doc_1_" + id}
                                                    checked={props.management ? !v.shareManagement : !v.share}
                                                    onChange={(e) => {
                                                        if(e.target.checked) {
                                                            if(props.management) {
                                                                setDocObj((curDocObj) => ({...curDocObj, [k]: { ...curDocObj[k], shareManagement: false }}));
                                                            }
                                                            else {
                                                                setDocObj((curDocObj) => ({...curDocObj, [k]: { ...curDocObj[k], share: false }}));
                                                            }
                                                        }
                                                    }}
                                            />

                                            <RadioCheckButton tiny={true} smallFont={true} type="radio" text={locale.msgs.SHARE}
                                                    id={k + "_free_doc_2_" + id} name={k + "_free_doc_2_" + id}
                                                    checked={props.management ? v.shareManagement : v.share}
                                                    onChange={(e) => {
                                                        if(e.target.checked) {
                                                            if(props.management) {
                                                                setDocObj((curDocObj) => ({...curDocObj, [k]: { ...curDocObj[k], shareManagement: true }}));
                                                            }
                                                            else {
                                                                setDocObj((curDocObj) => ({...curDocObj, [k]: { ...curDocObj[k], share: true }}));
                                                            }
                                                        }
                                                    } }
                                            />

                                            { props.management ?
                                                v.fromCustomer && (!vRevised || (vRevised && vRevised.filename == "")) ?
                                                    <div className="doc_upload_revised_arrow_box"
                                                            onClick={() => {
                                                                setDocObj((curDocObj) => ({...curDocObj, [k]: {...curDocObj[k], revisedShown: !v.revisedShown }}));

                                                                let keyRevised = kRevised;

                                                                if(v.revisedShown) {
                                                                    if(vRevised.onServer) {
                                                                        addFileToDeleteList(kRevised);
                                                                    }

                                                                    setDocObj((curDocObj) => {
                                                                        const newDocObj = {...curDocObj};
                                                                        delete newDocObj[keyRevised];
                                                                        return newDocObj;
                                                                    });
                                                                }
                                                                else {
                                                                    setDocObj((curDocObj) => {
                                                                        return {...curDocObj, [keyRevised]: new Document(v.name +
                                                                                                                        " (" + locale.msgs.REVISED + ")")};
                                                                    });
                                                                }
                                                            }}>
                                                        <img className="doc_upload_revised_arrow" src={v.revisedShown ? "/common/icon_arrow-up.svg" : "/common/icon_arrow-down.svg" } />
                                                    </div>
                                                :
                                                    null
                                            :
                                                <HelpBtn onClick={(e) => setDocObj((curDocObj) => ({...curDocObj, [k]: { ...curDocObj[k], shareHelpDisplayed: true }})) } />
                                            }
                                        </div>

                                        <div className="share_help_btn_box">
                                            <ClosePopup helpDisplayed={v.shareHelpDisplayed}
                                                    onClose={() => setDocObj((curDocObj) => ({...curDocObj, [k]: { ...curDocObj[k], shareHelpDisplayed: false }})) }
                                                    content={
                                                        <div className="share_help_content">
                                                            
                                                            <div className="text_big_3 margin_top_low_low">{locale.msgs.SHARE_DOCUMENTS}</div>
                                                            <div className="text_light_2 margin_top_low_low">{locale.msgs.SHARE_DOCUMENTS_DESCR}</div>
                                                        </div>
                                                    }
                                            />
                                        </div>

                                    </div>
                                    
                                    : null
                                }
                            </div>
                        </div>
                    :
                        null
                    }

                    { releaseInfoVisible ?
                        (props.management ? v.shareManagement : v.share) ?
                            <div className={"document_share_suc_box" + (vRevised && vRevised.filename != "" ? " document_share_suc_box_revised" : "")}>
                                <img className="passing_released_img" src="/common/icon_done_green.svg"></img>
                                <div className="passing_released_img_text text_success_2">
                                    {props.management ? locale.msgs.PASSING_RELEASED_MANAGEMENT : locale.msgs.PASSING_RELEASED}
                                </div>
                            </div>
                        :
                            <div className={"document_share_suc_box" + (vRevised && vRevised.filename != "" ? " document_share_suc_box_revised" : "")}>
                                <img className="passing_released_img_err" src="/common/icon_close_red.svg"></img>
                                <div className="passing_released_img_text passing_released_img_text_err text_error_2">
                                    {props.management ? locale.msgs.PASSING_NOT_RELEASED_MANAGEMENT : locale.msgs.PASSING_NOT_RELEASED}
                                </div>
                            </div>
                    :
                        null
                    }

                </div>
            )
        }

        result = (
            <div className="documents_box_box">
                { goodConductCertificateUrl !== null ?
                    <div className="good_conduct_certificate_box">
                        <div className="good_conduct_certificate_head">
                            <div className="text_light_2">{props.management  ? "" : locale.msgs.GOOD_CONDUCT_CERTIFICATE}</div>
                            <HelpBtn onClick={(e) => setGoodConductHelpDisplayed(true) } />

                            
                        </div>

                        <ClosePopup helpDisplayed={goodConductHelpDisplayed}
                                    onClose={() => setGoodConductHelpDisplayed(false) }
                                    content={
                                        <div className="share_help_content">
                                            {/* Using two separate text properties as target="_blank" can not be used inside the .json language files */}

                                            <div className="text_light_2 margin_top_low_low" dangerouslySetInnerHTML={{__html: locale.msgs.GOOD_CONDUCT_CERTIFICATE_TOOLTIP_DESCR_1}}></div>

                                            {/* Do not use LinkSmart as we leave the page */}
                                            <Link className="link_2 text_2" to="https://www.fuehrungszeugnis.bund.de/ffw/action/invoke.do?id=AntragFZ" target="_blank">www.fuehrungszeugnis.bund.de</Link>

                                            <div className="text_light_2 margin_top_low_low" dangerouslySetInnerHTML={{__html: locale.msgs.GOOD_CONDUCT_CERTIFICATE_TOOLTIP_DESCR_2}}></div>
                                        </div>
                                    }
                            />

                        <UploadInput name={""} filename={goodConductCertificateName}
                                editable={false} state={UploadInputState.UPLOADED}
                                url={goodConductCertificateUrl}
                                removeBtnPresent={false}
                                removable={false}
                        />
                    </div>
                :
                    null
                }

                <div className="editable_documents_head">
                    <div className="text_light_2">{!props.management && goodConductCertificateUrl !== null ? locale.msgs.YOUR_DOCUMENTS : ""}</div>

                    { !props.readOnly ?
                        <SafeEditBtn safeEdit={safeEdit} onClick={handleEdit} editable={editable} showTooltip={!props.management} />
                    :
                        null
                    }
                </div>

                <div ref={safeEdit.editBoxRef} className={ "documents_box" + (editable ? " documents_box_edit" : "") }>

                    { !props.readOnly ?
                        <AddButton2 text={locale.msgs.ADD_DOCUMENTS} editable={editable} onClick={handleAddClick} />
                    :
                        null
                    }

                    { boxes && boxes.length > 0 ?
                        boxes
                    :
                        <div className="text_light_2 no_files">{locale.msgs.NO_FILES}</div>
                    }
                </div>

                { !props.readOnly ?
                    <div className="doc_btn_box_box">
                        <div className="doc_btn_box">
                            <SaveCancelBox editable={editable} savable={isSavableVar} saving={docsSaving} saved={docsSaved}
                                    notSaved={docsNotSaved} onSaveClick={handleDocsSaveClick} onCancelClick={handleCancel}
                                    lockNavigation={true}
                            />
                        </div>

                        <div className="text_error_2 doc_btn_err_text">{errText ? errText : ""}</div>
                    </div>
                :
                    null
                }
                
            </div>
        );
    }

    return result;
}
